import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/models/user';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { PipeModule } from 'src/app/pipes/pipes.module';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { KeyboardArrows } from 'src/app/consts';
import { getEmailValidationErrors } from 'src/app/common/utils/form-validators';
import { DirectivesModule } from 'src/app/directives/directives.module';
import { UserSearchInputDirective } from 'src/app/directives/user-search-input.directive';
import { UserSearchSelectionService } from 'src/app/services/user-search-selection.service';
import { PencilSpinnerComponent } from 'src/app/ui/pencil-spinner/pencil-spinner.component';
import { PencilButtonComponent } from '../pencil-button/pencil-button.component';
import { IconTypes, PencilIconComponent } from '../pencil-icon/pencil-icon.component';
import { PencilPillComponent } from '../pencil-pill/pencil-pill.component';

@UntilDestroy()
@Component({
  selector: 'app-pencil-user-search-tags',
  templateUrl: './pencil-user-search-tags.component.html',
  styleUrls: ['./pencil-user-search-tags.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserSearchSelectionService],
  imports: [
    CommonModule,
    PencilButtonComponent,
    FormsModule,
    PencilIconComponent,
    PipeModule,
    TranslateModule,
    PencilPillComponent,
    MatMenuModule,
    DirectivesModule,
    UserSearchInputDirective,
    PencilSpinnerComponent,
  ],
})
export class PencilUserSearchTagsComponent implements OnInit {
  @Output() emitUsersSelected = new EventEmitter<User[]>();

  @ViewChild('userSearch') userSearch!: ElementRef;
  @ViewChildren('searchResultOption') userElements!: QueryList<ElementRef>;
  @ViewChild(UserSearchInputDirective) userSearchDirective?: UserSearchInputDirective;
  @Input() placeholder = 'Add user';

  private searchQuerySubject = new BehaviorSubject<string>('');
  public searchQuerySubject$ = this.searchQuerySubject.asObservable().pipe(map((s) => s.trim()));
  isSearchFocused = false;

  private _isValidEmail$ = new BehaviorSubject<boolean>(false);
  public isValidEmail$ = this._isValidEmail$.asObservable();

  private _searchTextBoxWidth$ = new BehaviorSubject<number>(85);
  public searchTextBoxWidth$ = this._searchTextBoxWidth$.asObservable();

  focusedUserIndex = -1;

  @Input()
  set selectedUsers(value: User[]) {
    this.userSearchSelectionService.initializeSelectedUsers(value ?? []);
  }

  currentUser: User | undefined = undefined;

  constructor(
    public userService: UserService,
    public cd: ChangeDetectorRef,
    public userSearchSelectionService: UserSearchSelectionService,
  ) {}

  ngOnInit(): void {
    this.prepareUserData();
    this.searchQuerySubject
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((model) => {
        if (model.length === 0) {
          this._isValidEmail$.next(false);
          return;
        }
        this.updateValidEmail(model);
        this.getInputScrollWidth();
      });
  }

  onInputChange(value: string): void {
    this.searchQuerySubject.next(value);
  }

  onBlurSearch(): void {
    this.isSearchFocused = false;
  }

  removeUser(user: User): void {
    this.userSearchSelectionService.removeSelectedUsers([user]);
    this.emitUsersSelected.emit([...(this.userSearchSelectionService.selectedUsers as User[])]);
  }

  selectUser(user: User): void {
    this.searchQuerySubject.next('');
    this.userSearchDirective?.clear();
    this.userSearchSelectionService.addSelectedUsers([user]);
    this.emitUsersSelected.emit([...(this.userSearchSelectionService.selectedUsers as User[])]);
  }

  onEmailClick(): void {
    const user = {
      email: this.searchQuerySubject.value,
    };

    this.selectUser(user as User);
  }

  prepareUserData(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((userData) => {
      this.currentUser = userData?.user;
    });
  }

  invokeNavigationWithArrowKeys(event: KeyboardEvent): void {
    switch (event.key) {
      case KeyboardArrows.DOWN:
        event.preventDefault();
        this.focusNextUser();
        break;
      case KeyboardArrows.UP:
        event.preventDefault();
        this.focusPreviousUser();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.focusedUserIndex >= 0) {
          const users = this.userSearchDirective?.filteredUsers ?? [];
          if (users[this.focusedUserIndex]) {
            this.selectUser(users[this.focusedUserIndex]);
          }
        }
        break;
      default:
        break;
    }
  }

  getInputScrollWidth() {
    this._searchTextBoxWidth$.next(this.userSearch.nativeElement.scrollWidth);
  }

  private scrollToUser(): void {
    if (this.focusedUserIndex >= 0) {
      this.userElements.toArray()[this.focusedUserIndex].nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  private focusNextUser(): void {
    const len = this.userSearchDirective?.filteredUsers?.length ?? 0;
    if (this.focusedUserIndex < len - 1) {
      this.focusedUserIndex++;
      this.scrollToUser();
    }
  }

  private focusPreviousUser(): void {
    if (this.focusedUserIndex > 0) {
      this.focusedUserIndex--;
      this.scrollToUser();
    }
  }

  private updateValidEmail(email: string): void {
    this._isValidEmail$.next(getEmailValidationErrors(email) === null);
  }

  trackById(index: number, user: User): string {
    return user._id;
  }

  protected IconTypes = IconTypes;
}
